import React, { Suspense, useMemo, type FC } from 'react';
import { ErrorBoundary } from '@funtech-int/rival-react';
import { PaymentPlace, useAuthManager } from '@funtech-int/yango-shared';

import { InfoBlockFullscreenNotification } from '@/shared/components/InfoBlockFullscreenNotification/InfoBlockFullscreenNotification';
import { PageSpinner } from '@/shared/components/PageSpinner';
import { ShowcaseId } from '@/shared/lib/analytics/showcase';
import { PlusPaymentPlace } from '@/shared/constants/payments';

import { TvChannelsInfoBlock } from '../multi-offer-info-blocks/TvChannelsInfoBlock/TvChannelsInfoBlock';
import { BenefitsInfoBlock } from '../multi-offer-info-blocks/BenefitsInfoBlock/BenefitsInfoBlock';
import { HomeShowcase } from '../HomeShowcase/HomeShowcase';
import { HomeFaq } from '../HomeFaq/HomeFaq';
import { LoginwallMainSection } from '../LoginwallMainSection/LoginwallMainSection';
import { useMultiOfferTab } from '../../hooks/useMultiOfferTab';
import { useYangoOnboardingConfig } from '../../hooks/useYangoOnboardingConfig';
import { useMultiOffers } from '../../hooks/useMultiOffers';
import { MultiOfferMainSection } from '../MultiOfferMainSection/MultiOfferMainSection';
import { getEmptyOffer, getPlaceByMultiOfferTab } from '../../lib/offers';
import { PaywallProvider } from '../../hooks/usePaywallContext';
import { PaywallMainSection } from '../PaywallMainSection/PaywallMainSection';

import styles from './HomeComponent.module.css';

const OnboardingBlocks: FC = () => {
    const config = useYangoOnboardingConfig();

    const shouldShowInfoBlocks = Boolean(config?.isInfoBlocksSectionEnabled);

    return (
        <>
            {shouldShowInfoBlocks && (
                <>
                    <TvChannelsInfoBlock />
                    <BenefitsInfoBlock />

                    <InfoBlockFullscreenNotification />
                </>
            )}
            <HomeFaq />
        </>
    );
};

interface PaywallComponentProps {
    forcedPlace?: PlusPaymentPlace;
    paymentPlace?: PaymentPlace;
    isFallbackOffer: boolean;
}

const PaywallComponent: FC<PaywallComponentProps> = ({
    forcedPlace,
    paymentPlace,
    isFallbackOffer,
}) => {
    const { tabId } = useMultiOfferTab();
    const place = forcedPlace ?? getPlaceByMultiOfferTab(tabId);

    const currentOffer = paymentPlace?.selectOfferBy({ place });
    const contextPaymentPlace = isFallbackOffer ? undefined : paymentPlace;
    const hasOffers = paymentPlace?.offers && Object.keys(paymentPlace?.offers).length > 0;
    const contextOffer = !hasOffers ? getEmptyOffer() : currentOffer;

    const paywallComponent = useMemo(() => {
        if (!hasOffers) return <PaywallMainSection />;
        if (isFallbackOffer) return <MultiOfferMainSection isFallback />;

        return <MultiOfferMainSection />;
    }, [hasOffers, isFallbackOffer]);

    return (
        <PaywallProvider offer={contextOffer} paymentPlace={contextPaymentPlace}>
            {paywallComponent}
            <OnboardingBlocks />
        </PaywallProvider>
    );
};

const PaywallContainer = () => {
    const { forcedPlace, paymentPlace, isFallbackOffer } = useMultiOffers();

    return (
        <PaywallComponent
            forcedPlace={forcedPlace}
            paymentPlace={paymentPlace}
            isFallbackOffer={isFallbackOffer}
        />
    );
};

const Loginwall: FC = () => (
    <PaywallProvider offer={getEmptyOffer()} paymentPlace={undefined}>
        <LoginwallMainSection />
        <OnboardingBlocks />
    </PaywallProvider>
);

interface HomeComponentProps {
    hasSubscription?: boolean;
}

export const HomeComponent: FC<HomeComponentProps> = ({ hasSubscription }: HomeComponentProps) => {
    const { isAuthorized } = useAuthManager();

    if (!isAuthorized) {
        return <Loginwall />;
    }

    if (!hasSubscription) {
        return (
            <ErrorBoundary
                fallback={
                    <PaywallComponent
                        forcedPlace={undefined}
                        paymentPlace={undefined}
                        isFallbackOffer
                    />
                }
            >
                <Suspense fallback={<PageSpinner noOffset />}>
                    <PaywallContainer />
                </Suspense>
            </ErrorBoundary>
        );
    }

    return (
        <main className={styles.main}>
            <HomeShowcase id={ShowcaseId.HOME} showLoading />
        </main>
    );
};
